import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import type { AlertColor } from "@mui/material/Alert";
import { useTranslation } from "react-i18next";

type BulkSelectionConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  /** Sync or async (e.g. `mutateAsync`); resolved values are ignored. */
  onConfirm: () => unknown;
  isPending: boolean;
  confirmColor?: "inherit" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  /** Defaults to `common.confirm` */
  confirmLabel?: string;
  alertSeverity?: AlertColor;
};

export function BulkSelectionConfirmDialog({
  open,
  onClose,
  title,
  description,
  onConfirm,
  isPending,
  confirmColor = "primary",
  confirmLabel,
  alertSeverity = "warning",
}: BulkSelectionConfirmDialogProps) {
  const { t } = useTranslation();

  const handleClose = () => {
    if (!isPending) {
      onClose();
    }
  };

  return (
    <Dialog
      aria-describedby="bulk-selection-confirm-description"
      aria-labelledby="bulk-selection-confirm-title"
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
    >
      <DialogTitle id="bulk-selection-confirm-title">{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Alert icon={<WarningAmberOutlinedIcon fontSize="inherit" />} severity={alertSeverity}>
            {t("delivery.table.bulkConfirm.alert")}
          </Alert>
          <Typography
            color="text.secondary"
            id="bulk-selection-confirm-description"
            variant="body2"
          >
            {description}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
        <Button color="inherit" disabled={isPending} onClick={handleClose} variant="outlined">
          {t("common.actions.cancel")}
        </Button>
        <Button
          color={confirmColor}
          disabled={isPending}
          onClick={() => {
            void onConfirm();
          }}
          variant="contained"
        >
          {isPending ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            (confirmLabel ?? t("common.confirm"))
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
